import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export const Pool3DSceneVanilla = () => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 10;
    camera.position.y = 0;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(10, 10, 5);
    scene.add(directionalLight);

    const blueLight = new THREE.PointLight(0x38bdf8, 0.5, 100);
    blueLight.position.set(-10, -10, -10);
    scene.add(blueLight);

    // Materials
    const pinkMaterial = new THREE.MeshStandardMaterial({
      color: 0xec4899,
      roughness: 0.3,
      metalness: 0.1,
    });

    const whiteMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.4,
      transparent: true,
      opacity: 0.9,
    });

    const metalMaterial = new THREE.MeshStandardMaterial({
      color: 0x94a3b8,
      roughness: 0.2,
      metalness: 0.8,
    });

    const waterMaterial = new THREE.MeshStandardMaterial({
      color: 0x38bdf8,
      roughness: 0.1,
      metalness: 0.2,
      transparent: true,
      opacity: 0.8,
    });

    const bubbleMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0,
      metalness: 0.1,
      transparent: true,
      opacity: 0.4,
    });

    // ==================== 3D Objects ====================

    // Pool Float (Torus)
    const floatGroup = new THREE.Group();
    const floatGeometry = new THREE.TorusGeometry(1, 0.4, 16, 32);
    const float = new THREE.Mesh(floatGeometry, pinkMaterial);
    floatGroup.add(float);
    
    const stripeGeometry = new THREE.TorusGeometry(1, 0.15, 8, 32);
    const stripe = new THREE.Mesh(stripeGeometry, whiteMaterial);
    stripe.rotation.x = Math.PI / 2;
    stripe.position.z = 0.2;
    floatGroup.add(stripe);
    
    floatGroup.position.set(4, 1.5, -3);
    scene.add(floatGroup);

    // Beach Ball
    const ballGroup = new THREE.Group();
    const ballGeometry = new THREE.SphereGeometry(0.7, 32, 32);
    const ballBase = new THREE.Mesh(ballGeometry, whiteMaterial);
    ballGroup.add(ballBase);

    // Ball segments
    const segmentGeometry = new THREE.SphereGeometry(0.72, 32, 32, 0, Math.PI * 0.5);
    const colors = [0xef4444, 0x3b82f6, 0xfbbf24, 0x22c55e];
    colors.forEach((color, i) => {
      const segmentMaterial = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.3,
        side: THREE.DoubleSide,
      });
      const segment = new THREE.Mesh(segmentGeometry, segmentMaterial);
      segment.rotation.y = (Math.PI / 2) * i;
      ballGroup.add(segment);
    });
    
    ballGroup.position.set(-4, 1, -2);
    scene.add(ballGroup);

    // Pool Ladder
    const ladderGroup = new THREE.Group();
    const railGeometry = new THREE.CylinderGeometry(0.05, 0.05, 3, 16);
    
    const leftRail = new THREE.Mesh(railGeometry, metalMaterial);
    leftRail.position.x = -0.4;
    ladderGroup.add(leftRail);
    
    const rightRail = new THREE.Mesh(railGeometry, metalMaterial);
    rightRail.position.x = 0.4;
    ladderGroup.add(rightRail);

    const stepGeometry = new THREE.CylinderGeometry(0.04, 0.04, 0.8, 16);
    [-0.8, -0.3, 0.2, 0.7].forEach((y) => {
      const step = new THREE.Mesh(stepGeometry, metalMaterial);
      step.rotation.z = Math.PI / 2;
      step.position.y = y;
      ladderGroup.add(step);
    });

    ladderGroup.position.set(5, -0.5, -4);
    scene.add(ladderGroup);

    // Water Drops
    const waterDrops = [];
    const dropGeometry = new THREE.SphereGeometry(0.25, 16, 16);
    const dropPositions = [
      [-2, 2.5, 0],
      [0, 3, -1],
      [2, 2.5, 0.5],
      [-1, 2, 0.5],
      [1, 2.5, -0.5],
    ];
    dropPositions.forEach((pos, i) => {
      const drop = new THREE.Mesh(dropGeometry, waterMaterial);
      drop.position.set(...pos);
      drop.userData = { initialY: pos[1], delay: i * 0.5 };
      scene.add(drop);
      waterDrops.push(drop);
    });

    // Bubbles
    const bubbles = [];
    for (let i = 0; i < 15; i++) {
      const size = 0.1 + Math.random() * 0.15;
      const bubbleGeometry = new THREE.SphereGeometry(size, 16, 16);
      const bubble = new THREE.Mesh(bubbleGeometry, bubbleMaterial);
      const x = (Math.random() - 0.5) * 12;
      const y = (Math.random() - 0.5) * 6 - 2;
      const z = (Math.random() - 0.5) * 4 - 2;
      bubble.position.set(x, y, z);
      bubble.userData = { 
        initialY: y, 
        initialX: x,
        speed: 0.5 + Math.random(),
        amplitude: 0.2 + Math.random() * 0.3 
      };
      scene.add(bubble);
      bubbles.push(bubble);
    }

    // Scroll progress
    let scrollProgress = 0;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      scrollProgress = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Animation
    const clock = new THREE.Clock();
    
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();

      // Animate Pool Float
      floatGroup.rotation.x = Math.sin(time * 0.5) * 0.1 + scrollProgress * Math.PI * 0.5;
      floatGroup.rotation.y = time * 0.3 + scrollProgress * Math.PI;
      floatGroup.rotation.z = Math.cos(time * 0.3) * 0.1;
      floatGroup.position.y = 1.5 + Math.sin(time) * 0.3 - scrollProgress * 4;
      floatGroup.position.x = 4 + scrollProgress * 1;

      // Animate Beach Ball
      ballGroup.rotation.x = time * 2 + scrollProgress * Math.PI * 2;
      ballGroup.rotation.y = time * 1.5;
      ballGroup.rotation.z = scrollProgress * Math.PI;
      ballGroup.position.y = 1 + Math.sin(time * 2) * 0.4 + scrollProgress * 3;
      ballGroup.position.x = -4 + Math.sin(scrollProgress * Math.PI) * 1.5;

      // Animate Ladder
      ladderGroup.rotation.y = Math.sin(time * 0.5) * 0.1 + scrollProgress * 0.4;
      ladderGroup.rotation.x = scrollProgress * 0.2;
      ladderGroup.position.y = -0.5 - scrollProgress * 5;

      // Animate Water Drops
      waterDrops.forEach((drop) => {
        const t = time + drop.userData.delay;
        drop.position.y = drop.userData.initialY + Math.sin(t * 2) * 0.5 - scrollProgress * 6;
        drop.rotation.z = Math.sin(t) * 0.3;
        const scale = 0.8 + Math.sin(t * 3) * 0.2;
        drop.scale.set(scale, scale, scale);
      });

      // Animate Bubbles
      bubbles.forEach((bubble) => {
        const t = time * bubble.userData.speed;
        bubble.position.y = bubble.userData.initialY + Math.sin(t) * bubble.userData.amplitude + scrollProgress * 4;
        bubble.position.x = bubble.userData.initialX + Math.sin(t * 0.7) * 0.2;
      });

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 5 }}
      data-testid="pool-3d-scene"
    />
  );
};

export default Pool3DSceneVanilla;
